/**
 * Canonical advertisement placement dimension specs.
 *
 * Single source of truth is the `token/dimensions.json` file - update that
 * file whenever a placement's canvas size changes and these helpers will pick
 * it up automatically.
 */
import dimensionsJson from "./dimensions.json";

/**
 * How closely an uploaded image's shape (aspect ratio) must match the
 * placement's shape to be accepted. 0.05 = the image's shape must be at least
 * ~95% identical to the spot's shape (pixel size is irrelevant).
 */
export const ASPECT_RATIO_TOLERANCE = 0.05;

export interface AdPlacementSpec {
  /** Machine key matching the `AdPlacement` enum (e.g. "in-feed"). */
  key: string;
  /** Human friendly name used in the UI (e.g. "Header"). */
  name: string;
  /** Exact recommended canvas width in pixels. */
  width: number;
  /** Exact recommended canvas height in pixels. */
  height: number;
  /** Human friendly aspect ratio (e.g. "97:5"). */
  aspectRatio: string;
  /** Numeric width / height ratio used for comparisons. */
  ratio: number;
}

export interface AdPlacementRequirement {
  /** Exact recommended canvas width in pixels. */
  width: number;
  /** Exact recommended canvas height in pixels. */
  height: number;
  /** Legacy alias for the exact canvas width (kept for backward compatibility). */
  minWidth: number;
  /** Legacy alias for the exact canvas height (kept for backward compatibility). */
  minHeight: number;
  /** Human friendly label including recommended size. */
  label: string;
  /** Human friendly aspect ratio (e.g. "97:5"). */
  ratio?: string;
}

type RawPlacement = {
  key?: string;
  name?: string;
  width?: number;
  height?: number;
  aspectRatio?: string;
};

const normalizeKey = (value: string): string => {
  const trimmed = value.trim().toLowerCase().replace(/\s+/g, "-");
  return trimmed === "infeed" ? "in-feed" : trimmed;
};

const rawPlacements: RawPlacement[] =
  (dimensionsJson as { adPlacements?: RawPlacement[] }).adPlacements || [];

export const PLACEMENT_SPECS: AdPlacementSpec[] = rawPlacements
  .filter(
    (item) =>
      typeof item.width === "number" &&
      typeof item.height === "number" &&
      item.width > 0 &&
      item.height > 0,
  )
  .map((item) => {
    const width = item.width as number;
    const height = item.height as number;
    const key = normalizeKey(item.key || item.name || "");
    const name = item.name || key;

    return {
      key,
      name,
      width,
      height,
      aspectRatio: item.aspectRatio || `${width}:${height}`,
      ratio: width / height,
    };
  });

export const PLACEMENT_DIMENSIONS: Record<string, AdPlacementRequirement> =
  PLACEMENT_SPECS.reduce(
    (acc, spec) => {
      acc[spec.key] = {
        width: spec.width,
        height: spec.height,
        minWidth: spec.width,
        minHeight: spec.height,
        label: `${spec.name} (${spec.width}×${spec.height}px)`,
        ratio: spec.aspectRatio,
      };
      return acc;
    },
    {} as Record<string, AdPlacementRequirement>,
  );

export const getPlacementSpec = (key?: string): AdPlacementSpec | undefined => {
  if (!key) return undefined;
  const normalized = normalizeKey(key);
  return PLACEMENT_SPECS.find((spec) => spec.key === normalized);
};

export type FitStatus = "success" | "error" | "none";

export interface FitAssessment {
  /** True when the image shape is accepted for this placement. */
  ok: boolean;
  status: FitStatus;
  /**
   * Shape similarity in percent, where 100 means the image has exactly the
   * same aspect ratio as the spot (pixel size is not part of this score).
   */
  matchPercent: number;
  /** Plain-language explanation that everyday users can understand. */
  message: string;
}

const shapeOrientation = (spec: AdPlacementSpec): string => {
  if (spec.ratio > 1.02) return "wide, flat (landscape)";
  if (spec.ratio < 0.98) return "tall (portrait)";
  return "square-ish";
};

/**
 * Checks whether an uploaded image matches the placement's target aspect ratio.
 *
 * The goal is to keep the spot's exact aspect ratio (e.g. 19.4 : 1 for the
 * header). The physical pixel size is flexible - what matters is that the image
 * is roughly as many times wide as it is tall as the spot. The image is
 * accepted when its shape is at least ~95% identical to the spot's shape.
 */
export const assessPlacementFit = (
  width: number,
  height: number,
  placement?: string,
): FitAssessment => {
  const spec = getPlacementSpec(placement);
  if (!spec) {
    return { ok: true, status: "none", matchPercent: 100, message: "" };
  }

  const uploadedRatio = width / height;
  const shapeSimilarity = Math.min(
    uploadedRatio / spec.ratio,
    spec.ratio / uploadedRatio,
  );
  const matchPercent = Math.round(shapeSimilarity * 100);
  const deviation = Math.abs(uploadedRatio - spec.ratio) / spec.ratio;

  if (deviation <= ASPECT_RATIO_TOLERANCE) {
    return {
      ok: true,
      status: "success",
      matchPercent,
      message: `Image matches the ${spec.name} aspect ratio (${spec.aspectRatio}). It will display cleanly without distortion.`,
    };
  }

  // Calculate quick target dimensions based on uploaded width
  const targetHeight = Math.max(1, Math.round(width / spec.ratio));

  return {
    ok: false,
    status: "error",
    matchPercent,
    message: `Invalid aspect ratio for ${spec.name} (${spec.aspectRatio}). The image will look stretched or cropped. Crop your image to ${width} × ${targetHeight}px (or ${spec.width} × ${spec.height}px) for this placement.`,
  };
};
